import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { ChevronDown } from 'lucide-react';

import css from './Select.module.css';

/**
 * Select component - Custom dropdown select matching Radix UI pattern
 *
 * @component
 * @param {Object} props
 * @param {string} [props.value] - The selected value
 * @param {Function} props.onValueChange - Callback when value changes
 * @param {Array<{value: string, label: string}>} props.options - Array of select options
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.className] - Custom class name
 * @param {boolean} [props.disabled] - Whether select is disabled
 * @returns {JSX.Element}
 */
const Select = ({ value, onValueChange, options, placeholder = 'Select...', className, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue) => {
    onValueChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div ref={selectRef} className={classNames(css.select, className)}>
      <button
        type="button"
        className={classNames(css.trigger, {
          [css.triggerOpen]: isOpen,
          [css.triggerDisabled]: disabled,
        })}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={css.value}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={classNames(css.icon, { [css.iconOpen]: isOpen })} />
      </button>

      {isOpen && (
        <div className={css.content}>
          <div className={css.viewport}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={classNames(css.item, {
                  [css.itemSelected]: option.value === value,
                })}
                onClick={() => handleSelect(option.value)}
              >
                <span className={css.itemText}>{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Select;
